import React, { useEffect, useState, lazy } from 'react';
import { ChevronLeft, Copy, Share2, Star, BookOpen } from 'lucide-react';
import { ScenarioData } from './ScenarioWizard';
interface Step5RevealProps {
  scenarioData: ScenarioData;
  onComplete: () => void;
  onBack: () => void;
}
export const Step5Reveal: React.FC<Step5RevealProps> = ({
  scenarioData,
  onComplete,
  onBack
}) => {
  const [copied, setCopied] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  // Call onComplete when the component mounts to show media generation options
  useEffect(() => {
    // Call onComplete to show media generation options
    onComplete();
  }, [onComplete]);
  // Simulate audio playback
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setAudioProgress(prev => {
          if (prev >= 100) {
            setIsPlaying(false);
            return 100;
          }
          return prev + 1;
        });
      }, 300);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying]);
  const copyToClipboard = () => {
    navigator.clipboard.writeText(getScenarioFullText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handlePlayAudio = () => {
    if (audioProgress === 100) {
      setAudioProgress(0);
    }
    setIsPlaying(!isPlaying);
  };
  const getScenarioFullText = () => {
    const themeTexts: Record<string, string> = {
      lazy_morning: `The sheets are warm against my skin as I stretch lazily in bed. I can feel your eyes on me as the morning light filters through the curtains, casting a soft glow across my body. My hair is tousled from sleep, and the thin fabric of my ${scenarioData.outfit === 'lingerie' ? 'lace lingerie' : 'silk nightgown'} leaves little to the imagination.\n\n"Good morning," I whisper, turning to face you with a smile that promises more than just breakfast. "Did you sleep well? Or should we find a better way to start the day?" I reach out to trace my fingers along your chest, my touch light but deliberate.\n\nThe world outside can wait. Right now, all that matters is us, the warmth of our bodies, and the delicious anticipation of what's to come. "Should I keep this on," I ask, tugging playfully at my lingerie, "or would you prefer to unwrap your morning gift yourself?"`,
      hot_tub: `Steam rises around me as I sink deeper into the bubbling water of the hot tub. The ${scenarioData.location === 'hotel' ? "hotel's private jacuzzi" : 'secluded hot tub'} offers the perfect escape from prying eyes. I've been waiting for you, and my patience is wearing thin.\n\nAs you approach, I let my gaze wander over you appreciatively. "I was beginning to think you weren't coming," I say, my voice a playful purr. The water laps against my skin, and I make no effort to hide the fact that beneath the surface, I'm wearing nothing at all.\n\n"The water's perfect," I continue, beckoning you closer with a crooked finger. "But it would be even better with you in it." I shift slightly, allowing you a glimpse of wet skin glistening in the dim light. "Don't make me wait any longer. I've been thinking about this moment all day."`,
      club_seduction: `The music pulses through my body as I move on the dance floor of the crowded club. The beat is intoxicating, or maybe it's the way you've been watching me from across the room that has my blood racing. I'm wearing a ${scenarioData.outfit === 'dress' ? 'tight dress that hugs every curve' : 'revealing outfit that leaves little to the imagination'}, and I know exactly what effect it's having on you.\n\nOur eyes meet across the room, and I give you a look that says everything words can't. With a slight tilt of my head, I invite you to join me. The lights flash, casting colorful shadows across my face as I continue to dance, my movements becoming more deliberate, more sensual, knowing you're watching.\n\nAs you make your way through the crowd, I turn my back to you, still dancing, waiting for the moment when I feel your hands on my hips, your breath on my neck. The anticipation is almost unbearable. "I was wondering how long it would take you to come over," I say, just loud enough to be heard over the music. "What took you so long?"`,
      naughty_nurse: `I adjust my nurse's uniform, making sure the buttons strain just enough to catch your attention. The white fabric is crisp against my skin, the skirt just short enough to be unprofessional. "The doctor will see you now," I say with a mischievous smile, clipboard in hand as I lead you into the examination room.\n\nOnce inside, I close the door behind us, turning the lock with a decisive click. "Now then," I say, my professional demeanor slipping just enough to reveal something more playful underneath, "let's take a look at your... symptoms."\n\nI approach slowly, each step deliberate, the click of my heels against the floor marking the rhythm of your accelerating heartbeat. "I think you need a very... thorough examination," I continue, setting the clipboard aside and reaching for my stethoscope. "I'll need you to remove your shirt. Doctor's orders." My fingers brush against your skin as I help you, lingering just a moment too long to be purely medical.`,
      beach_day: `The sun warms my skin as I lie on the towel, the sound of waves crashing nearby a soothing backdrop to our private beach day. I'm wearing a ${scenarioData.outfit === 'bikini' ? 'bikini that leaves little to the imagination' : 'swimsuit that hugs every curve'}, and I've caught you stealing glances more than once.\n\nI watch you emerge from the water, droplets cascading down your body, and I can't help but bite my lip in appreciation. "Enjoying the view?" I ask, knowing full well the answer. I sit up, adjusting my sunglasses, and pat the space beside me on the oversized beach towel.\n\n"I've got sunscreen," I say, holding up the bottle with a playful smile. "Would you mind? There are some spots I just can't reach myself." I turn over, exposing my back, and glance over my shoulder with an invitation in my eyes. "Don't be shy. I don't bite... unless you want me to."`,
      office_affair: `I lean against your desk, twirling a pen between my fingers as I watch you work. The office is empty except for us, the late hour ensuring our privacy. "Working late again?" I ask, my voice breaking the silence. I'm dressed in a ${scenarioData.outfit === 'dress' ? "form-fitting dress that's just professional enough for the office" : 'blouse and skirt combination that walks the line between business and pleasure'}.\n\nSlowly, I begin unbuttoning my blouse, just enough to draw your attention. "You know, all work and no play..." I let the sentence hang in the air between us, a promise of what could happen if you just set aside those reports.\n\n"Everyone's gone home," I continue, moving closer, the scent of my perfume filling the space between us. "It's just you and me now. Should we use your desk or mine?" I perch on the edge of your desk, crossing my legs slowly, deliberately, watching your reaction with a satisfied smile. "I've been thinking about this all day. Haven't you?"`
    };
    // Personalize with angel name if available
    let text = themeTexts[scenarioData.theme] || 'Your personalized fantasy is now ready to explore...';
    if (scenarioData.angelName) {
      // Replace first person references with the angel's name where appropriate
      text = text.replace(/^I /gm, `${scenarioData.angelName} `);
      text = text.replace(/ I /g, ` ${scenarioData.angelName} `);
      text = text.replace(/\nI /g, `\n${scenarioData.angelName} `);
      // Replace "my" with "[name]'s" in some cases
      text = text.replace(/my skin/g, `${scenarioData.angelName}'s skin`);
      text = text.replace(/my voice/g, `${scenarioData.angelName}'s voice`);
      text = text.replace(/my hair/g, `${scenarioData.angelName}'s hair`);
      // But keep first-person dialogue
      text = text.replace(new RegExp(`"([^"]*${scenarioData.angelName}[^"]*)"`, 'g'), (match, p1) => {
        return `"${p1.replace(scenarioData.angelName, 'I')}"`;
      });
    }
    return text;
  };
  return <div className="space-y-6" data-id="element-22075">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-22076">
        Your Fantasy Is Ready
      </h2>
      <p className="text-gray-700 dark:text-gray-300" data-id="element-22077">
        {scenarioData.angelName ? `Your personalized scenario with ${scenarioData.angelName} has been created. Enjoy this immersive experience!` : 'Your personalized fantasy scenario has been created. Enjoy this immersive experience!'}
      </p>

      {/* Audio Player (if voice narration was selected) */}
      {scenarioData.voice && scenarioData.voice !== 'none' && <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-22078">
          <div className="flex items-center gap-3 mb-3" data-id="element-22079">
            <button onClick={handlePlayAudio} className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-500 rounded-full text-white" data-id="element-22080">
              {isPlaying ? <span className="h-3 w-3 bg-white rounded-sm" data-id="element-22081"></span> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-22082">
                  <polygon points="5 3 19 12 5 21 5 3" data-id="element-22083"></polygon>
                </svg>}
            </button>
            <div className="flex-1" data-id="element-22084">
              <div className="flex items-center justify-between mb-1" data-id="element-22085">
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-22086">
                  {scenarioData.theme.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-22087">
                  {Math.floor(audioProgress * 0.3)}:
                  {Math.floor(audioProgress * 0.3 % 1 * 60).toString().padStart(2, '0')}{' '}
                  / 30:00
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-22088">
                <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full transition-all" style={{
              width: `${audioProgress}%`
            }} data-id="element-22089"></div>
              </div>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400" data-id="element-22090">
            <span data-id="element-22091">
              Voice: {scenarioData.voice === 'female' ? 'Female' : 'Male'}
            </span>
            <span data-id="element-22092">
              Quality:{' '}
              {scenarioData.soundQuality === 'high' ? 'High' : 'Standard'}
            </span>
            {scenarioData.includeSexSounds && <span data-id="element-22093">Sex Sounds: Enabled</span>}
          </div>
        </div>}

      <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 relative" data-id="element-22094">
        <div className="absolute top-4 right-4 flex space-x-2" data-id="element-22095">
          <button onClick={copyToClipboard} className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" title={copied ? 'Copied!' : 'Copy to clipboard'} data-id="element-22096">
            <Copy size={14} data-id="element-22097" />
          </button>
          <button className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" title="Share scenario" data-id="element-22098">
            <Share2 size={14} data-id="element-22099" />
          </button>
        </div>
        <div className="prose dark:prose-invert max-w-none" data-id="element-22100">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-22101">
            {scenarioData.theme === 'lazy_morning' ? 'Lazy Morning' : scenarioData.theme === 'hot_tub' ? 'Hot Tub Night' : scenarioData.theme === 'club_seduction' ? 'Club Seduction' : scenarioData.theme === 'naughty_nurse' ? 'Naughty Nurse' : scenarioData.theme === 'beach_day' ? 'Beach Day' : 'Office Affair'}
          </h3>
          <div className="whitespace-pre-line text-gray-800 dark:text-gray-200" data-id="element-22102">
            {getScenarioFullText()}
          </div>
        </div>
      </div>

      {/* Scenario Details */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-22103">
        <h3 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-22104">
          Scenario Details
        </h3>
        <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-22105">
          <div className="text-gray-700 dark:text-gray-300" data-id="element-22106">
            <span className="font-medium" data-id="element-22107">Theme:</span>{' '}
            {scenarioData.theme.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
          <div className="text-gray-700 dark:text-gray-300" data-id="element-22108">
            <span className="font-medium" data-id="element-22109">Location:</span>{' '}
            {scenarioData.location.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
          <div className="text-gray-700 dark:text-gray-300" data-id="element-22110">
            <span className="font-medium" data-id="element-22111">Outfit:</span>{' '}
            {scenarioData.outfit.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
          <div className="text-gray-700 dark:text-gray-300" data-id="element-22112">
            <span className="font-medium" data-id="element-22113">Tone:</span>{' '}
            {scenarioData.tone.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
          {scenarioData.sexActs && scenarioData.sexActs.length > 0 && <div className="col-span-2 text-gray-700 dark:text-gray-300" data-id="element-22114">
              <span className="font-medium" data-id="element-22115">Sex Acts:</span>{' '}
              {scenarioData.sexActs.join(', ')}
            </div>}
        </div>
      </div>

      <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800/30" data-id="element-22116">
        <div className="flex items-center" data-id="element-22117">
          <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-500 mr-4" data-id="element-22118">
            <Star size={20} data-id="element-22119" />
          </div>
          <div data-id="element-22120">
            <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-22121">
              You've earned 100 Intimacy XP!
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-22122">
              Creating personalized scenarios deepens your connection
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4" data-id="element-22123">
        <button onClick={onBack} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center" data-id="element-22124">
          <ChevronLeft size={16} className="mr-1" data-id="element-22125" />
          <span data-id="element-22126">Back</span>
        </button>
        <div className="flex space-x-2" data-id="element-22127">
          <button onClick={() => {
          // Save the scenario to user's collection
          onComplete();
          // Navigate to the scenario collection
          const navigateEvent = new CustomEvent('navigate', {
            detail: {
              section: 'social_feed'
            }
          });
          document.dispatchEvent(navigateEvent);
        }} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-teal-600 transition-all shadow-md shadow-blue-500/20 flex items-center" data-id="element-22128">
            <BookOpen size={16} className="mr-2" data-id="element-22129" />
            <span data-id="element-22130">Save to Collection</span>
          </button>
          <button onClick={() => {
          // Navigate to chat with this angel if available
          if (scenarioData.angelName) {
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'chat'
              }
            });
            document.dispatchEvent(navigateEvent);
            // Set angel as active chat partner (using a dummy ID since we don't have the actual ID)
            const selectAngelEvent = new CustomEvent('selectAngel', {
              detail: {
                angelId: '1' // This should ideally be the actual angel ID
              }
            });
            document.dispatchEvent(selectAngelEvent);
          } else {
            onComplete();
          }
        }} className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-all shadow-md shadow-pink-500/20 flex items-center" data-id="element-22131">
            <span data-id="element-22132">
              {scenarioData.angelName ? `Chat with ${scenarioData.angelName}` : 'Complete'}
            </span>
          </button>
        </div>
      </div>
    </div>;
};